import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import Loader from '../../Loader';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { getCorpAndPCListByMgntGrp } from '../../../api';
import { getCorpIdFromUrl } from '../../../APIs/auth';

const cellBorderStyle = {
  borderBottom: '1px solid #f2f2f2',
  borderRight: '1px solid #f2f2f2',
  padding: '6px 8px',
};

const tableHeaderCellSx = {
  background: 'linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%)',
  borderTop: '1px solid #f2f2f2',
  borderBottom: '1px solid #f2f2f2',
  borderRight: '1px solid #f2f2f2',
  fontWeight: 500,
  fontSize: '0.75rem',
  color: '#000',
  padding: '6px 8px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const tableBodyCellSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#626262',
  ...cellBorderStyle,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const tableContainerSx = {
  height: '100%',
  width: '100%',
  backgroundColor: '#ffffff',
  border: '1px solid #f2f2f2',
  borderRadius: 1,
  overflowX: 'hidden',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: 5 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: 10 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9e9e9e' },
};


const CorporationSelectionModal = ({
  open,
  onClose,
  selectedCorporationIds = [],
  onApply,
  onApplyAndGenerate,
  initialManagementGroup = 'all',
  customManagementGroups = [],
}) => {
  const activeGroups = customManagementGroups;
  const [selectedIds, setSelectedIds] = useState(selectedCorporationIds);
  const [managementGroup, setManagementGroup] = useState(initialManagementGroup);
  const [searchTerm, setSearchTerm] = useState('');
  const [corporationsList, setCorporationsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync internal selected IDs when modal opens with new incoming props
  useEffect(() => {
    if (open) {
      const urlCorpId = getCorpIdFromUrl();
      if (
        (!selectedCorporationIds || selectedCorporationIds.length === 0) &&
        urlCorpId &&
        urlCorpId !== '000000000000000000000000000000000000' &&
        urlCorpId.toLowerCase() !== 'all' &&
        urlCorpId !== 'null' &&
        urlCorpId !== 'undefined' &&
        corporationsList.length > 0
      ) {
        const matchedRows = corporationsList.filter(
          (item) =>
            String(item.CorporationID || '').toLowerCase() === urlCorpId.toLowerCase() ||
            String(item.id || '').toLowerCase().startsWith(urlCorpId.toLowerCase())
        );
        if (matchedRows.length > 0) {
          setSelectedIds(matchedRows.map((r) => r.id));
        } else {
          setSelectedIds(selectedCorporationIds);
        }
      } else {
        setSelectedIds(selectedCorporationIds);
      }
      setManagementGroup(initialManagementGroup);
      setSearchTerm('');
    }
  }, [open, selectedCorporationIds, initialManagementGroup, corporationsList]);

  // Fetch corporations by Management Group ID from API
  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    const fetchCorps = async () => {
      setIsLoading(true);
      try {
        const mgntGrpId = managementGroup || 'PropertyType';
        const data = await getCorpAndPCListByMgntGrp(mgntGrpId);
        if (isMounted) {
          const list = Array.isArray(data) ? data : [];
          setCorporationsList(list);

          // If no corporation IDs are currently selected, auto-select by URL corpId if available
          const urlCorpId = getCorpIdFromUrl();
          if (
            (!selectedCorporationIds || selectedCorporationIds.length === 0) &&
            urlCorpId &&
            urlCorpId !== '000000000000000000000000000000000000' &&
            urlCorpId.toLowerCase() !== 'all' &&
            urlCorpId !== 'null' &&
            urlCorpId !== 'undefined'
          ) {
            const matchedRows = list.filter(
              (item) =>
                String(item.CorporationID || '').toLowerCase() === urlCorpId.toLowerCase() ||
                String(item.id || '').toLowerCase().startsWith(urlCorpId.toLowerCase())
            );
            if (matchedRows.length > 0) {
              const matchedIds = matchedRows.map((r) => r.id);
              setSelectedIds(matchedIds);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching corporations from API:', err);
        if (isMounted) {
          setCorporationsList([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCorps();

    return () => {
      isMounted = false;
    };
  }, [open, managementGroup, selectedCorporationIds]);

  // Filter dataset by global search term across all 6 text columns
  const filteredRecords = useMemo(() => {
    let list = corporationsList;

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      list = list.filter(
        (item) =>
          (item.CorporationName || item.dbaName || '').toLowerCase().includes(query) ||
          (item.PCName || item.legalName || '').toLowerCase().includes(query) ||
          (item.PropertyType || item.brandType || '').toLowerCase().includes(query) ||
          (item.Service || item.service || '').toLowerCase().includes(query) ||
          (item.Brand || item.brand || '').toLowerCase().includes(query) ||
          (item.PMS || item.pms || '').toLowerCase().includes(query)
      );
    }

    return list;
  }, [corporationsList, searchTerm]);

  // Checkbox state for visible records
  const isAllVisibleSelected = useMemo(() => {
    if (filteredRecords.length === 0) return false;
    return filteredRecords.every((record) => selectedIds.includes(record.id));
  }, [filteredRecords, selectedIds]);

  const isSomeVisibleSelected = useMemo(() => {
    if (filteredRecords.length === 0) return false;
    const count = filteredRecords.filter((record) => selectedIds.includes(record.id)).length;
    return count > 0 && count < filteredRecords.length;
  }, [filteredRecords, selectedIds]);

  const handleToggleSelectAll = useCallback(() => {
    const visibleIds = filteredRecords.map((r) => r.id);
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      } else {
        const set = new Set([...prev, ...visibleIds]);
        return Array.from(set);
      }
    });
  }, [filteredRecords]);

  const handleToggleRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const handleApply = useCallback(() => {
    const selectedRecords = corporationsList.filter((r) => selectedIds.includes(r.id));
    onApply(selectedIds, selectedRecords);
    onClose();
  }, [selectedIds, corporationsList, onApply, onClose]);

  const handleApplyAndGenerate = useCallback(async () => {
    const selectedRecords = corporationsList.filter((r) => selectedIds.includes(r.id));
    if (onApplyAndGenerate) {
      await onApplyAndGenerate(selectedIds, selectedRecords);
    } else {
      onApply(selectedIds, selectedRecords);
    }
    onClose();
  }, [selectedIds, corporationsList, onApply, onApplyAndGenerate, onClose]);

  // Selected count text display
  const selectedCountText = useMemo(() => {
    const count = selectedIds.length;
    if (count === 0) return '0 corporations selected';
    if (count === 1) return '1 corporation selected';
    return `${count} corporations selected`;
  }, [selectedIds]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#F5F7FA',
          minHeight: '100vh',
        },
      }}
    >
      {/* 1. Top Title & Actions Bar */}
      <Box
        sx={{
          p: 1,
          px: 1.5,
          borderBottom: '1px solid #d9d9d9',
          backgroundColor: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Title & Close Box */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '2px',
              border: '1px solid #797979',
              backgroundColor: '#797979',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: '#555555',
                borderColor: '#555555',
                color: 'white',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '14px' }} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 550, fontSize: '1.15rem', color: '#2782EC' }}>
            Corporation Selection
          </Typography>
        </Box>

        {/* Action Buttons Top Right */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            sx={{
              textTransform: 'none',
              backgroundColor: '#a7d2ea',
              color: '#333',
              fontSize: '0.75rem',
              fontWeight: 600,
              px: 2.5,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#a7d2ea',
              },
            }}
          >
            Apply
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApplyAndGenerate}
            sx={{
              textTransform: 'none',
              backgroundColor: '#a7d2ea',
              color: '#333',
              fontSize: '0.75rem',
              fontWeight: 600,
              px: 2.5,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#a7d2ea',
              },
            }}
          >
            Apply & Generate
          </Button>
        </Stack>
      </Box>

      {/* 2. Distinct Filter Toolbar Bar */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #d9d9d9',
          backgroundColor: '#ffffff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
        >
          {/* Management Group Dropdown */}
          <FormControl size="small" sx={{ minWidth: 220, flex: { xs: '1 1 auto', sm: '0 0 240px' } }}>
            <InputLabel id="modal-mg-label" sx={{ fontSize: '0.85rem' }}>
              Management Group
            </InputLabel>
            <Select
              labelId="modal-mg-label"
              id="modal-management-group"
              value={activeGroups.some((g) => g.ValueCode === managementGroup) ? managementGroup : ''}
              label="Management Group"
              onChange={(e) => setManagementGroup(e.target.value)}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 550,
                height: 40,
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
              }}
            >
              {activeGroups.map((group) => (
                <MenuItem key={group.ValueCode} value={group.ValueCode} sx={{ fontSize: '0.75rem' }}>
                  {group.DisplayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Selected Count Indicator */}
          <Box
            sx={{
              px: 2,
              height: 40,
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                color: '#111827',
              }}
            >
              {selectedCountText}
            </Typography>
          </Box>

          {/* Global Search Bar */}
          <TextField
            size="small"
            placeholder="Search by Corporation, Legal Name, Brand, Service, PMS..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: '1 1 auto',
              width: '100%',
              maxWidth: 550,
              '& .MuiOutlinedInput-root': {
                height: 40,
                fontSize: '0.75rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Stack>
      </Box>

      {/* Grid Content / Table with 7 Columns */}
      <DialogContent sx={{ p: 0, flex: '1 1 auto', minHeight: 0, overflow: 'hidden', backgroundColor: '#F8F9FA' }}>
        <TableContainer component={Box} sx={tableContainerSx}>
          <Table stickyHeader size="small" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {/* 1. Checkbox Header */}
                <TableCell
                  sx={{ ...tableHeaderCellSx, width: '4%', minWidth: 40, textAlign: 'center', p: '2px', cursor: 'pointer' }}
                  onClick={handleToggleSelectAll}
                >
                  <Checkbox
                    size="small"
                    checked={isAllVisibleSelected}
                    indeterminate={isSomeVisibleSelected}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      p: 1,
                      color: '#626262',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: '#2782EC',
                      },
                      pointerEvents: 'none',
                    }}
                  />
                </TableCell>

                {/* 2. Corporation Name / DBA Name */}
                <TableCell sx={{ ...tableHeaderCellSx, width: '24%' }}>
                  Corporation Name / DBA Name
                </TableCell>

                {/* 3. Legal Name */}
                <TableCell sx={{ ...tableHeaderCellSx, width: '21%' }}>
                  Legal Name
                </TableCell>

                {/* 4. Brand Type */}
                <TableCell sx={{ ...tableHeaderCellSx, width: '13%' }}>
                  Brand Type
                </TableCell>

                {/* 5. Service */}
                <TableCell sx={{ ...tableHeaderCellSx, width: '14%' }}>
                  Service
                </TableCell>

                {/* 6. Brand */}
                <TableCell sx={{ ...tableHeaderCellSx, width: '12%' }}>
                  Brand
                </TableCell>

                {/* 7. PMS */}
                <TableCell sx={{ ...tableHeaderCellSx, width: '12%', borderRight: 'none' }}>
                  PMS
                </TableCell>
              </TableRow>
            </TableHead>


            <TableBody>
              {isLoading && filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ ...tableBodyCellSx, py: 4, color: '#626262', textAlign: 'center' }}>
                    Loading corporations...
                  </TableCell>
                </TableRow>
              ) : filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ ...tableBodyCellSx, py: 4, color: '#9e9e9e', textAlign: 'center' }}>
                    No corporations found matching your search / filter criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((row) => {
                  const isSelected = selectedIds.includes(row.id);

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleToggleRow(row.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#eef6ff' : '#ffffff',
                        '&:hover': {
                          backgroundColor: isSelected ? '#dbeafe' : '#f8f9fa',
                        },
                      }}
                    >
                      {/* 1. Row Checkbox */}
                      <TableCell sx={{ ...tableBodyCellSx, textAlign: 'center', p: '2px' }}>
                        <Checkbox

                          size="small"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            p: 1,
                            '&.Mui-checked': {
                              color: '#2782EC',
                            },
                            pointerEvents: 'none',
                          }}
                        />
                      </TableCell>

                      {/* 2. Corporation Name / DBA Name */}
                      <TableCell sx={{ ...tableBodyCellSx, color: '#212529', fontWeight: 550 }} title={row.CorporationName || row.dbaName}>
                        {row.CorporationName || row.dbaName}
                      </TableCell>

                      {/* 3. Legal Name / PC Name */}
                      <TableCell sx={tableBodyCellSx} title={row.PCName || row.legalName}>
                        {row.PCName || row.legalName}
                      </TableCell>

                      {/* 4. Brand Type / Property Type */}
                      <TableCell sx={tableBodyCellSx} title={row.PropertyType || row.brandType}>
                        {row.PropertyType || row.brandType}
                      </TableCell>

                      {/* 5. Service */}
                      <TableCell sx={tableBodyCellSx} title={row.Service || row.service}>
                        {row.Service || row.service}
                      </TableCell>

                      {/* 6. Brand */}
                      <TableCell sx={tableBodyCellSx} title={row.Brand || row.brand}>
                        {row.Brand || row.brand}
                      </TableCell>

                      {/* 7. PMS */}
                      <TableCell sx={{ ...tableBodyCellSx, borderRight: 'none' }} title={row.PMS || row.pms}>
                        {row.PMS || row.pms}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <Loader loading={isLoading} />
    </Dialog>
  );
};

export default CorporationSelectionModal;
